"use client";

import { useEffect, useRef, useState } from "react";
import { TAMERS, tamerSrc } from "./data";
import { c, useReducedMotion } from "./hooks";

// 67-tamer carousel: 4.4 s autoplay (off under reduced motion, while hovered or
// focused, or when paused with the Ⅱ button), prev/next, ← → keys, touch swipe,
// and a snap-scrolling thumb roster that follows user-driven changes.

const pad = (n: number) => String(n).padStart(2, "0");

export default function Tamers() {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [held, setHeld] = useState(false); // pointer over / focus within
  const [restartKey, setRestartKey] = useState(0);
  const [userAction, setUserAction] = useState(0);
  const thumbs = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const touchX = useRef(0);
  const n = TAMERS.length;
  const t = TAMERS[index], prev = TAMERS[(index - 1 + n) % n], next = TAMERS[(index + 1) % n];

  useEffect(() => {
    if (reduced || userPaused || held) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % n), 4400);
    return () => window.clearInterval(id);
  }, [reduced, userPaused, held, restartKey, n]);

  useEffect(() => {
    if (!userAction) return;
    const el = thumbs.current?.children[index] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "nearest", inline: "center" });
  }, [userAction, index, reduced]);

  const show = (i: number) => { setIndex(((i % n) + n) % n); setUserAction((k) => k + 1); setRestartKey((k) => k + 1); };
  const count = `${pad(index + 1)} / ${pad(n)}`;

  return (
    <div className={c("dojo-panel")}>
      <div ref={card} className={c("tamer-card")} tabIndex={0} role="region" aria-roledescription="carousel" aria-label="Mojumon Crimson playable tamers"
        onPointerEnter={() => setHeld(true)} onPointerLeave={() => setHeld(false)}
        onFocus={() => setHeld(true)} onBlur={(e) => { if (!card.current?.contains(e.relatedTarget as Node | null)) setHeld(false); }}
        onKeyDown={(e) => { if (e.key === "ArrowLeft") { e.preventDefault(); show(index - 1); } if (e.key === "ArrowRight") { e.preventDefault(); show(index + 1); } }}
        onTouchStart={(e) => { touchX.current = e.changedTouches[0].clientX; }}
        onTouchEnd={(e) => { const d = e.changedTouches[0].clientX - touchX.current; if (Math.abs(d) > 45) show(d > 0 ? index - 1 : index + 1); }}>
        <div className={c("tamer-status")}><span>Roster online</span><span>{count}</span></div>
        <div className={c("tamer-viewport")}>
          <button className={c("tamer-nav", "prev")} type="button" aria-label="Previous tamer" onClick={() => show(index - 1)}>‹</button>
          <div className={c("tamer-stage")}>
            <img className={c("tamer-side")} src={tamerSrc(prev)} alt="" aria-hidden="true" />
            <div className={c("tamer-focus")}><img key={index} className={c("tamer-sprite", "tamer-in")} src={tamerSrc(t)} alt={`${t.name} Mojumon Crimson tamer sprite`} /></div>
            <img className={c("tamer-side")} src={tamerSrc(next)} alt="" aria-hidden="true" />
          </div>
          <button className={c("tamer-nav", "next")} type="button" aria-label="Next tamer" onClick={() => show(index + 1)}>›</button>
        </div>
        <div className={c("tamer-controls")}>
          <button className={c("tamer-toggle")} type="button" aria-label={userPaused ? "Play tamer slideshow" : "Pause tamer slideshow"} aria-pressed={userPaused} onClick={() => { setUserPaused((p) => !p); setRestartKey((k) => k + 1); }}>{userPaused ? "▶" : "Ⅱ"}</button>
          <span className={c("tamer-page")}>TAMER {count}</span>
        </div>
      </div>
      <div className={c("tamer-copy")}>
        <p className={c("section-kicker", "pixel-font")}>More than an avatar</p>
        <h2>Control the Dojo Tamers</h2>
        <p>Familiar faces from the Dojo community become playable Tamers in Crimson. Swipe, tap, or use the arrows to explore every look.</p>
        <div className={c("tamer-file")}>
          <span className={c("tamer-role")}>{t.role}</span>
          <h3 className={c("tamer-name")}>{t.name}</h3>
          <span className={c("tamer-number")}>Crimson File {String(index + 1).padStart(3, "0")}</span>
          <p key={index} className={c("tamer-description", "tamer-in")}>{t.description}</p>
        </div>
        <div className={c("tamer-tags")}><span>Playable Tamers</span><span>Community Characters</span><span>{n} Looks</span></div>
      </div>
      <div className={c("tamer-roster")} style={{ gridColumn: "1 / -1" }}>
        <div className={c("roster-head")}><span>Select a Tamer</span><span>Swipe the roster ↔</span></div>
        <div ref={thumbs} className={c("tamer-thumbs")} aria-label="Choose a Tamer">
          {TAMERS.map((tm, i) => (
            <button key={tm.file} className={c("tamer-thumb", i === index && "active")} type="button" aria-label={`Show ${tm.name}, ${tm.role}`} aria-current={i === index ? "true" : "false"} onClick={() => show(i)}>
              <img src={tamerSrc(tm)} alt="" loading="lazy" />
            </button>))}
        </div>
      </div>
    </div>
  );
}
